import React, { useState, useEffect } from 'react';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { Space, Button, Select, Input, Form, Modal, Tooltip, AutoComplete } from 'antd';
import { NotificationInstance } from 'antd/es/notification/interface';

import { InfoCircleOutlined, PlusOutlined } from '@ant-design/icons';

import IBaseComponentProps from '@components/base-component-props';

import { exception } from '@extensions/notification';
import { IOption } from '@extensions/option';

import { useAppDispatch } from '@store/hooks';
import { userLoaded } from '@store/actions';

import { ITradingPlatform } from '@entities/trading-platform';
import { ICountry } from '@entities/country';

dayjs.extend(utc);

interface ITradingPlatformParams extends IBaseComponentProps {
    defaultPlatformName: string | undefined;
    basePlatforms: Array<ITradingPlatform> | undefined;
    country: ICountry | undefined;
    disabled?: boolean;
    hideAdd?: boolean;
    api: NotificationInstance;
    onSelect: (value: string) => void;
    loadPlatforms: () => void;
}

const TradingPlatform = (props: ITradingPlatformParams) => {
    const { basePlatforms, defaultPlatformName, api, disabled, hideAdd, country, loadPlatforms, onSelect, serverFetch } = props;

    const [platformForm] = Form.useForm();

    const d = useAppDispatch();

    const [platforms, setPlatforms] = useState<Array<ITradingPlatform>>([]);
    const [options, setOptions] = useState<Array<IOption>>([]);
    const [platformOpen, setPlatformOpen] = useState<boolean>(false);

    useEffect(() => {
        if (!basePlatforms) return;

        setPlatforms(basePlatforms);
    }, [basePlatforms]);

    useEffect(() => {
        const items: Array<IOption> = [];

        platforms.map((p: ITradingPlatform) => {
            const newItem: IOption = {
                value: `${p.name}`,
                label: `${p.name}`,
            };

            items.push(newItem);
        });

        setOptions(items);
    }, [platforms]);

    const onSavePlatform = (entity: ITradingPlatform) => {
        serverFetch(`tradingplatforms`, { method: 'POST', bodyData: entity })
            .then(() => {
                platformForm.resetFields();
                setPlatformOpen(false);
                loadPlatforms();
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка сохранения торговой площадки', ex, () => d(userLoaded(undefined)));
            });
    };

    return (
        <>
            <Space.Compact style={{ width: '100%' }}>
                <AutoComplete
                    allowClear
                    value={defaultPlatformName}
                    placeholder={'Торговая площадка'}
                    disabled={disabled}
                    popupMatchSelectWidth={false}
                    options={options}
                    onSelect={onSelect}
                    showSearch={{
                        onSearch: async (value: string) => {
                            const result = basePlatforms?.filter((p) => p.name?.toUpperCase()?.startsWith(value.toUpperCase()));
                            setPlatforms(result || []);
                        },
                    }}
                    suffix={
                        disabled && !country ? (
                            <Tooltip title='Укажите страну'>
                                <InfoCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
                            </Tooltip>
                        ) : (
                            ''
                        )
                    }
                />
                {!hideAdd && (
                    <Tooltip title='Добавить торговую площадку'>
                        <Button icon={<PlusOutlined />} onClick={() => setPlatformOpen(true)} />
                    </Tooltip>
                )}
            </Space.Compact>

            <Modal
                title='Торговая площадка'
                open={platformOpen}
                okText='ОК'
                onOk={() => platformForm.submit()}
                onCancel={() => {
                    platformForm.resetFields();

                    setPlatformOpen(false);
                }}
            >
                <Form colon={false} labelCol={{ span: 5 }} wrapperCol={{ span: 17 }} onFinish={onSavePlatform} form={platformForm}>
                    <Form.Item initialValue={country?.id} label='Страна' name='countryId'>
                        <Select autoFocus options={[{ value: country?.id, label: country?.name }]} disabled={true}></Select>
                    </Form.Item>
                    <Form.Item
                        required
                        label='Название'
                        name='name'
                        rules={[{ required: true, message: 'Укажите название торговой площадки' }]}
                    >
                        <Input
                            onInput={(e) => ((e.target as HTMLInputElement).value = (e.target as HTMLInputElement).value.toUpperCase())}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default TradingPlatform;
